import React, { useState, useEffect } from 'react';
import { SectionShell, TextAreaField, saveSection } from './shared';
import { apiGet } from '../../../lib/api';
import { Plus, Trash2, Link2 } from 'lucide-react';

const UNITS = ['Sqm','Sqft','Cum','Mtr','Rmt','Nos','MT','Kg','Bag','RM','RMT','LOT','LS'];

function emptyActivity() {
  return { description: '', wbs_id: '', wbs_name: '', wbs_code: '', unit: '', quantity: '' };
}

export function PlanningEditor({ data, dept, deptLabel, onSaved, projectId, date, readOnly }) {
  const [lookahead, setLookahead]   = useState(data.lookahead_notes || '');
  const [blockers, setBlockers]     = useState(data.blockers || '');
  const [saving, setSaving]         = useState(false);
  const [wbsList, setWbsList]       = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Planned activities — each is {description, wbs_id, wbs_name, unit, quantity, wbs_code}
  const [planned, setPlanned] = useState(() => {
    const raw = data.planned_activities || [];
    if (!raw.length) return [emptyActivity()];
    // Support legacy string format (plain text activities)
    return raw.map(a => typeof a === 'string'
      ? { ...emptyActivity(), description: a }
      : a
    );
  });

  // Fetch WBS activities due in next 14 days
  useEffect(() => {
    if (!projectId || !date) return;
    apiGet(`/dpr/${projectId}/${date}/planning-suggestions`)
      .then(r => setWbsList(r.activities || []))
      .catch(() => {});
  }, [projectId, date]);

  const update = (i, key, val) =>
    setPlanned(prev => prev.map((a, idx) => idx === i ? { ...a, [key]: val } : a));

  const linkWBS = (i, wbs) => {
    setPlanned(prev => prev.map((a, idx) => idx === i ? {
      ...a,
      wbs_id: wbs.id,
      wbs_name: wbs.name,
      wbs_code: wbs.wbs_code || '',
      unit: wbs.unit || a.unit,
      description: a.description || wbs.name,
    } : a));
  };

  const save = () => saveSection(projectId, date, dept, {
    lookahead_notes: lookahead,
    blockers,
    planned_activities: planned
      .map(a => ({ ...a, quantity: a.quantity ? parseFloat(a.quantity) : null }))
      .filter(a => a.description || a.wbs_id),
  }, onSaved, deptLabel, setSaving);

  return (
    <SectionShell deptLabel={deptLabel} dept={dept} onSave={save} saving={saving} readOnly={readOnly}>
      <TextAreaField
        label="This week's look-ahead plan"
        value={lookahead} onChange={setLookahead} rows={2}
        testid="dpr-planning-lookahead"
      />
      <TextAreaField
        label="Current blockers / delays"
        value={blockers} onChange={setBlockers} rows={2}
        testid="dpr-planning-blockers"
      />

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            Planned Activities
          </label>
          <div className="flex gap-3">
            {wbsList.length > 0 && (
              <button
                type="button"
                onClick={() => setShowSuggestions(s => !s)}
                className="text-xs text-[#3b4872] font-semibold flex items-center gap-1 hover:underline"
              >
                <Link2 size={11} />
                {showSuggestions ? 'Hide WBS' : `Link WBS (${wbsList.length})`}
              </button>
            )}
            <button
              type="button"
              onClick={() => setPlanned([...planned, emptyActivity()])}
              className="text-xs text-[#3b4872] font-semibold flex items-center gap-1 hover:underline"
              data-testid="dpr-planning-add"
            >
              <Plus size={11} /> Add
            </button>
          </div>
        </div>

        {/* WBS suggestions panel */}
        {showSuggestions && wbsList.length > 0 && (
          <div className="mb-3 border border-[#3b4872]/20 rounded-sm bg-[#3b4872]/5 p-2">
            <p className="text-[10px] text-slate-500 mb-1.5 font-bold uppercase tracking-wider">
              WBS activities due in next 14 days — tap to add:
            </p>
            <div className="space-y-1 max-h-44 overflow-y-auto">
              {wbsList.map(w => (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => {
                    // Fill empty last activity or append new
                    const last = planned[planned.length - 1];
                    if (!last.description && !last.wbs_id) {
                      linkWBS(planned.length - 1, w);
                    } else {
                      setPlanned(prev => [...prev, {
                        ...emptyActivity(),
                        wbs_id: w.id, wbs_name: w.name, wbs_code: w.wbs_code || '',
                        unit: w.unit || '', description: w.name,
                      }]);
                    }
                    setShowSuggestions(false);
                  }}
                  className="w-full text-left px-2 py-1.5 rounded-sm bg-white border border-slate-200
                             hover:border-[#3b4872] text-xs flex items-center justify-between gap-2"
                >
                  <div className="min-w-0">
                    <span className="font-medium text-slate-700 truncate block">
                      {w.wbs_code && <span className="text-slate-400 mr-1">[{w.wbs_code}]</span>}
                      {w.name}
                    </span>
                  </div>
                  <span className="text-slate-400 shrink-0 text-[10px]">
                    {w.pct_complete || 0}% · {w.unit} · due {w.planned_end?.slice(5)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Activity rows */}
        <div className="space-y-2">
          {planned.map((a, i) => (
            <div key={i} className="border border-slate-200 rounded-sm p-2 bg-white">
              {a.wbs_id && (
                <div className="flex items-center gap-1 mb-1.5">
                  <Link2 size={10} className="text-[#3b4872] shrink-0" />
                  <span className="text-[10px] text-[#3b4872] font-bold truncate">
                    {a.wbs_code && `[${a.wbs_code}] `}{a.wbs_name}
                  </span>
                  <button
                    type="button"
                    onClick={() => update(i, 'wbs_id', '')}
                    className="ml-auto text-slate-300 hover:text-rose-500 text-[10px] shrink-0"
                  >
                    unlink
                  </button>
                </div>
              )}
              <input
                value={a.description}
                onChange={e => update(i, 'description', e.target.value)}
                placeholder="Planned activity description"
                className="w-full border-0 border-b border-slate-100 text-sm px-0 py-1 focus:outline-none"
                data-testid={`dpr-planning-activity-${i}`}
              />
              <div className="flex items-center gap-2 mt-1.5">
                <input
                  type="number" min="0" step="0.01"
                  value={a.quantity}
                  onChange={e => update(i, 'quantity', e.target.value)}
                  placeholder="Qty"
                  className="w-24 border border-slate-200 rounded-sm text-xs px-2 py-1"
                  data-testid={`dpr-planning-qty-${i}`}
                />
                <select
                  value={a.unit}
                  onChange={e => update(i, 'unit', e.target.value)}
                  className="flex-1 border border-slate-200 rounded-sm text-xs px-2 py-1 bg-white"
                >
                  <option value="">Unit</option>
                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
                <button
                  type="button"
                  onClick={() => setPlanned(planned.filter((_, idx) => idx !== i))}
                  className="text-rose-400 hover:text-rose-600 p-0.5"
                  data-testid={`dpr-planning-remove-${i}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SectionShell>
  );
}
